import { cva, type VariantProps } from "class-variance-authority";
import { type ComponentProps, forwardRef } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Paper's explicit 4-point control scale layered on the generated shadcn
 * Button. Keep this outside components/ui so registry updates remain safe.
 */
const controlButtonSizes = cva("rounded-[8px]", {
  defaultVariants: { controlSize: "default" },
  variants: {
    controlSize: {
      compact: "h-7 gap-1 px-2.5 text-xs leading-4 [&_svg:not([class*='size-'])]:size-3.5",
      toolbar: "h-8 gap-1.5 px-3 text-xs leading-4 [&_svg:not([class*='size-'])]:size-3.5",
      default: "h-9 gap-1.5 px-3.5 text-[13px] leading-[18px] [&_svg:not([class*='size-'])]:size-4",
      form: "h-10 gap-2 px-4 text-sm [&_svg:not([class*='size-'])]:size-4",
      "icon-row": "size-6 p-0 [&_svg:not([class*='size-'])]:size-3.5",
      "icon-compact": "size-7 p-0 [&_svg:not([class*='size-'])]:size-3.5",
      "icon-toolbar": "size-8 p-0 [&_svg:not([class*='size-'])]:size-4",
      "icon-default": "size-9 p-0 [&_svg:not([class*='size-'])]:size-4",
      "icon-form": "size-10 p-0 [&_svg:not([class*='size-'])]:size-4",
    },
  },
});

export type ControlButtonProps = Omit<ComponentProps<typeof Button>, "size"> &
  VariantProps<typeof controlButtonSizes>;

export const ControlButton = forwardRef<HTMLButtonElement, ControlButtonProps>(
  function ControlButton({ className, controlSize, ...props }, ref): React.ReactElement {
    return (
      <Button ref={ref} className={cn(controlButtonSizes({ controlSize }), className)} {...props} />
    );
  },
);
